import { IConnection } from '../interfaces/models/connection'

const leanId = (value: unknown): string => {
    if (value == null) return ''
    if (typeof value === 'string') return value
    if (typeof value === 'object' && value !== null && '$oid' in value) {
        return String((value as { $oid: string }).$oid)
    }
    if (typeof value === 'object' && value !== null && '_id' in value) {
        return leanId((value as { _id: unknown })._id)
    }
    return String(value)
}

const participantMatchesConnection = (participant: any, connection: IConnection): boolean => {
    if (!participant || !connection) return false
    if (connection.profileId) {
        const pid = String(connection.profileId)
        if (participant.profileId === pid || participant.memberId === pid) return true
    }
    if (connection.publicIdentifier && participant.publicIdentifier === connection.publicIdentifier) {
        return true
    }
    const participantDocId = leanId(participant._id)
    if (participantDocId && leanId(connection._id) === participantDocId) return true
    return false
}

export const conversationMatchesConnection = (conversation: any, connection: IConnection): boolean => {
    if (!conversation || !connection?._id) return false
    const connectionId = String(connection._id)
    if (conversation.connectionId && String(conversation.connectionId) === connectionId) return true
    if (Array.isArray(conversation.connections)) {
        if (conversation.connections.some((id: unknown) => leanId(id) === connectionId)) return true
    }
    const participants = conversation.participants || []
    if (participants.length === 1) return participantMatchesConnection(participants[0], connection)
    return participants.some((p: any) => participantMatchesConnection(p, connection))
}

const mergeParticipantFromConnection = (participant: any, connection: IConnection) => ({
    ...participant,
    firstName: connection.firstName ?? participant.firstName,
    lastName: connection.lastName ?? participant.lastName,
    name:
        connection.name
        ?? [connection.firstName, connection.lastName].filter(Boolean).join(' ')
        ?? participant.name,
    profilePicture: connection.profilePicture ?? participant.profilePicture,
    headline: connection.headline ?? participant.headline,
    publicIdentifier: connection.publicIdentifier ?? participant.publicIdentifier,
    profileId: connection.profileId ?? participant.profileId,
    memberId: connection.profileId ?? participant.memberId,
})

export const patchParticipantsFromConnection = (
    participants: any[] | undefined,
    connection: IConnection,
) => {
    if (!participants?.length) return participants
    if (participants.length === 1) {
        return [mergeParticipantFromConnection(participants[0], connection)]
    }
    return participants.map((participant) =>
        participantMatchesConnection(participant, connection)
            ? mergeParticipantFromConnection(participant, connection)
            : participant,
    )
}

export const patchConversationFromConnection = (conversation: any, connection: IConnection) => {
    if (!conversationMatchesConnection(conversation, connection)) return conversation
    return {
        ...conversation,
        connectionId: conversation.connectionId || connection._id,
        // Inbox conversation tags may be updated optimistically (apply/remove). Prefer existing conversation.tags
        // so background connection refreshes don't re-introduce stale tag lists into the active thread.
        tags: conversation.tags ?? connection.tags,
        participants: patchParticipantsFromConnection(conversation.participants, connection),
    }
}

export const patchConversationsFromConnections = (
    conversations: any[],
    updatedConnections: IConnection[],
) => {
    if (!conversations?.length || !updatedConnections?.length) return { conversations, changed: false }
    let changed = false
    const next = conversations.map((conversation) => {
        let patched = conversation
        updatedConnections.forEach((connection) => {
            const candidate = patchConversationFromConnection(patched, connection)
            if (candidate !== patched) {
                patched = candidate
                changed = true
            }
        })
        return patched
    })
    return { conversations: next, changed }
}
